import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';
import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import PrePurchaseUpsellS12nEnrollModal from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellS12nEnrollModal';
import { showPrePurchaseUpsellFlow } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import { isFreemiumModelRolloutGAEnabled } from 'bundles/enroll-common/utils/freemiumModelUtils';
import S12nBulkPayEnrollModal from 'bundles/enroll-modal/components/bulk-pay/S12nBulkPayEnrollModal';
import CourseraPlusEnrollModal from 'bundles/enroll-modal/components/coursera-plus/CourseraPlusEnrollModal';
import NonRecurringEnrollModal from 'bundles/enroll-modal/components/non-recurring/NonRecurringEnrollModal';
import NonRecurringEnrollModalV2 from 'bundles/enroll-modal/components/non-recurring/NonRecurringEnrollModalV2';
import S12nPaymentMethodEnrollModal from 'bundles/enroll-modal/components/non-recurring/S12nPaymentMethodEnrollModal';
import S12nPreEnrollModal from 'bundles/enroll-modal/components/pre-enrollment/S12nPreEnrollModal';
import SubscriptionEnrollModal from 'bundles/enroll-modal/components/subscriptions/SubscriptionEnrollModal';
import { useS12nEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import EnterpriseEnrollmentChoiceModalForXDP from 'bundles/enterprise-legacy-learner-home/components/EnterpriseEnrollmentChoiceModalForXDP';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import GoogleCertificateEpicClient from 'bundles/epic/clients/GoogleCertificate';
import paymentsExperiments from 'bundles/epic/clients/payments';

export type PropsFromCaller = {
  s12nId: string;
  courseId?: string;
  partnerIds?: Array<string>;
  onSdp?: boolean;
  onClose: () => void;
  isPrePurchaseUpsellEligible?: boolean;
};

const S12nEnrollModal = ({
  s12nId,
  onClose,
  courseId,
  onSdp = false,
  isPrePurchaseUpsellEligible,
}: PropsFromCaller) => {
  const {
    enrollmentAvailableChoices,
    programs,
    invitedPrograms,
    thirdPartyOrganizations,
    invitedThirdPartyOrganizations,
    promotionEligibilities,
  } = useS12nEnrollModalData();

  const location = useLocation();

  const {
    canSubscribeToS12n,
    canEnrollThroughCourseraPlus,
    canEnrollThroughS12nPrepaid,
    canEnrollThroughProgram,
    canEnrollThroughProgramInvitation,
    canPurchaseThroughCourseraPlus,
    canSubscribeToCourseraPlus,
  } = enrollmentAvailableChoices;

  const canSeePaymentMethodModal = canEnrollThroughS12nPrepaid && canSubscribeToS12n;
  const isCourseraPlusPrepaidEnabled = requestCountry.get() === 'IN';
  const canSeeNonRecurringModal =
    (canEnrollThroughS12nPrepaid && !canSubscribeToS12n) ||
    (isCourseraPlusPrepaidEnabled && canPurchaseThroughCourseraPlus);
  const hasPromotion = Boolean(promotionEligibilities?.isEligible ? promotionEligibilities?.promoCodeId : undefined);

  const isEnterpriseUser = canEnrollThroughProgram || canEnrollThroughProgramInvitation;
  const waitForBothProgramDataFetch = canEnrollThroughProgram && canEnrollThroughProgramInvitation;

  const shouldShowPreEnrollModal =
    !paymentsExperiments.preview('disablePreEnrollment') &&
    (enrollmentAvailableChoices.canPreEnroll || enrollmentAvailableChoices.isPreEnrolled);

  // For the list of s12ns that are in the pre enrolled flow, show the pre enroll modal before any other s12n modal
  if (shouldShowPreEnrollModal && s12nId) {
    return (
      <S12nPreEnrollModal
        s12nId={s12nId}
        onSdp={onSdp}
        onClose={onClose}
        courseId={courseId}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
  } else if (isEnterpriseUser) {
    return (
      <EnterpriseEnrollmentChoiceModalForXDP
        thirdPartyOrganizations={thirdPartyOrganizations}
        programs={programs}
        invitedPrograms={invitedPrograms}
        invitedThirdPartyOrganizations={invitedThirdPartyOrganizations}
        productId={s12nId}
        productType={PRODUCT_TYPES.SPECIALIZATION}
        userId={user.get().id}
        handleClose={onClose}
        waitForBothProgramDataFetch={waitForBothProgramDataFetch}
      />
    );
    // Only trigger Coursera Plus flow for users who are already subscribed users
  } else if (canEnrollThroughCourseraPlus) {
    return <CourseraPlusEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} />;
  } else if (
    !hasPromotion &&
    canSubscribeToS12n &&
    canSubscribeToCourseraPlus &&
    showPrePurchaseUpsellFlow(location.pathname)
  ) {
    return <PrePurchaseUpsellS12nEnrollModal handleClose={onClose} onSdp={onSdp} />;
  } else if (canSeePaymentMethodModal) {
    // Recording impression for India price test.
    // Epic: https://tools.coursera.org/epic/experiment/WS16jhbwEfCHlw4giApofQ
    GoogleCertificateEpicClient.get('india-price-decrease');
    return <S12nPaymentMethodEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} onSdp={onSdp} />;
  } else if (canSeeNonRecurringModal) {
    // Recording impression for India price test.
    // Epic: https://tools.coursera.org/epic/experiment/WS16jhbwEfCHlw4giApofQ
    GoogleCertificateEpicClient.get('india-price-decrease');

    if (isFreemiumModelRolloutGAEnabled()) {
      return <NonRecurringEnrollModalV2 s12nId={s12nId} courseId={courseId} onClose={onClose} onSdp={onSdp} />;
    }

    return <NonRecurringEnrollModal s12nId={s12nId} courseId={courseId} onClose={onClose} onSdp={onSdp} />;
  } else if (canSubscribeToS12n) {
    return (
      <SubscriptionEnrollModal
        onClose={onClose}
        onSdp={onSdp}
        isPrePurchaseUpsellEligible={isPrePurchaseUpsellEligible}
      />
    );
  } else {
    return <S12nBulkPayEnrollModal onClose={onClose} onSdp={onSdp} />;
  }
};

export default S12nEnrollModal;
